/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import ErrorModal from "components/ErrorModal"
import ToastNotification from "components/ToastNotification"
import { UserParameters } from "features/userParameters/UserParametersSchema"
import { useCallback, useEffect, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"
import { parseWorkbookFile } from "services/workbooks/workbookParsingDelegation"

const XlsxUpload = () => {
  const formContext = useFormContext<UserParameters>()
  const { reset, trigger, clearErrors } = formContext
  const [uploadError, setUploadError] = useState<string | string[] | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [needsValidation, setNeedsValidation] = useState(false)
  const [uploadedFileName, setUploadedFileName] = useState("")
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Clean up toast timeout if component unmounts
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current)
      }
    }
  }, [])

  // This effect triggers form validation after the component has re-rendered.
  // By setting `needsValidation` to true in the file change handler, we ensure that
  // all state updates (including form reset) are applied before validation runs.
  useEffect(() => {
    if (needsValidation) {
      void (async () => {
        // trigger a revalidation of the form
        await trigger()
        setNeedsValidation(false)
      })()
    }
  }, [needsValidation, trigger, clearErrors])

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (!files || files.length === 0) {
        return
      }

      setIsUploading(true)
      setUploadError(null)

      // Clear any existing toast
      setShowSuccessToast(false)
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current)
      }

      const file = files[0]
      setUploadedFileName(file.name)

      try {
        const result = await parseWorkbookFile(file)

        if (Array.isArray(result)) {
          // Result is an array of error messages
          setUploadError(result)
        } else {
          reset(result)
          setNeedsValidation(true)
          setUploadError(null)

          // Show success toast and set auto-dismiss timer
          setShowSuccessToast(true)
          toastTimeoutRef.current = setTimeout(() => {
            setShowSuccessToast(false)
          }, 4000)
        }
      } catch (error) {
        setUploadError(error instanceof Error ? error.message : "Unknown error parsing file")
      } finally {
        setIsUploading(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
      }
    },
    [reset]
  )

  return (
    <>
      <div className="d-flex align-items-center">
        <button
          type="button"
          className="btn btn-secondary mt-3"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          Load
        </button>

        {isUploading && (
          <div className="spinner-border spinner-border-sm ms-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          className="d-none"
          id="xlsx-upload"
          accept=".xlsx"
          ref={fileInputRef}
          onChange={event => void handleFileChange(event)}
        />
      </div>

      <ToastNotification
        show={showSuccessToast}
        type="success"
        title="Success"
        message={
          <>
            Parameters successfully loaded from <strong>{uploadedFileName}</strong>
          </>
        }
        onClose={() => {
          setShowSuccessToast(false)
        }}
      />

      <ErrorModal
        show={!!uploadError}
        title="Error Importing File"
        errorMessages={uploadError ?? ""}
        onClose={() => {
          setUploadError(null)
        }}
      />
    </>
  )
}

export default XlsxUpload
